// src/components/SettingsTab.jsx
import React from 'react'
import { useTranslation } from 'react-i18next'
import { findPasswordColumn } from '../../../utils/excelProcessor'

export const getUploadTypeConfig = (uploadType, t) => {
    return uploadType === 'full_update'
        ? {
              title: t('Whole School Update'),
              description: t('Use this option when starting a new school year or setting up your school on Upstrive for the first time.'),
              color: 'text-darkPurple',
              icon: (
                  <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clip-path="url(#clip0_205_3878)">
                          <path
                              d="M1.5 3.99959V9.99959M1.5 9.99959H7.5M1.5 9.99959L6.14 5.63959C7.21475 4.5643 8.54437 3.77879 10.0048 3.35636C11.4652 2.93393 13.0089 2.88834 14.4917 3.22385C15.9745 3.55935 17.3482 4.26502 18.4845 5.27501C19.6209 6.28499 20.4828 7.56637 20.99 8.99959M23.5 19.9996V13.9996M23.5 13.9996H17.5M23.5 13.9996L18.86 18.3596C17.7853 19.4349 16.4556 20.2204 14.9952 20.6428C13.5348 21.0652 11.9911 21.1108 10.5083 20.7753C9.02547 20.4398 7.6518 19.7342 6.51547 18.7242C5.37913 17.7142 4.51717 16.4328 4.01 14.9996"
                              stroke="#585695"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                          />
                      </g>
                      <defs>
                          <clipPath id="clip0_205_3878">
                              <rect width="24" height="24" fill="white" transform="translate(0.5)" />
                          </clipPath>
                      </defs>
                  </svg>
              ),
              details: t('Existing users not in Excel will be disabled and old classes will be removed.'),
              backgroundColorAndBorder: 'bg-[#FDDF6D66] border border-[#FDDF6D]',
              backgroundColor: 'bg-[#FDDF6D66]',
          }
        : {
              title: t('Add New Users'),
              description: t('Use this option to add students or staff during the school year while keeping everything else the same.'),
              color: 'text-[#2F8946]',
              icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" color="#585695">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
              ),
              details: t('Only new users will be added. Existing users and classes remain unchanged.'),
              backgroundColorAndBorder: 'bg-[#D7EFDF] border border-[#2F8946]',
          }
}

function SettingsTab({ settings, onSettingsChange, studentsData, staffData, uploadType }) {
    const { t, locale } = useTranslation()
    const studentPasswordColumn = findPasswordColumn(studentsData)
    const staffPasswordColumn = findPasswordColumn(staffData)
    const hasPasswordColumns = studentPasswordColumn || staffPasswordColumn

    const handleToggle = (setting) => {
        // Don't allow toggling password setting if no password columns exist
        if (setting === 'useSheetPassword' && !hasPasswordColumns) {
            return
        }

        onSettingsChange({
            ...settings,
            [setting]: !settings[setting],
        })
    }

    const uploadConfig = getUploadTypeConfig(uploadType, t)

    return (
        <div className="w-full">
            {/* Header */}
            <div className="space-y-3">
                <h3 className="text-[31px] leading-[39px] tracking-tight  font-bold text-darkPurple mb-3">{t('Configure Import Settings')}</h3>
                <p className="text-darkPurple text-[14px] leading-[23px]  font-normal">{t('Fine-tune how your data will be imported based on your selected upload method')}</p>
            </div>

            {/* Upload Type Summary Card */}
            <div className={`${uploadConfig.backgroundColorAndBorder} rounded-[24px] p-3 mt-[42px] relative`}>
                <div className="flex items-center space-x-3">
                    <div className={`${uploadConfig.backgroundColor} w-[50px] h-[50px] rounded-full border border-[#585695] flex items-center justify-center`}>
                        {uploadConfig.icon}
                    </div>
                    <div className="flex-1 space-y-3 mr-[100px]">
                        <h4 className={`font-bold text-[16px] leading-5 text-darkPurple`}>
                            {t('Selected Method')}: {uploadConfig.title}
                        </h4>
                        <p className={`text-[16px] leading-[18px] font-medium text-deepPurple tracking-tight`}>{uploadConfig.description}</p>
                        <p className={`text-[16px] leading-[18px] font-medium text-deepPurple tracking-tight`}>{uploadConfig.details}</p>
                    </div>
                </div>
                <div
                    className={`absolute top-[19px] right-[20px] px-3 h-[36px] flex items-center justify-center bg-white ${uploadConfig.color} rounded-full text-[18px] leading-[18px] font-semibold tracking-tight`}
                >
                    {t('Active')}
                </div>
            </div>

            {/* Settings Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                {/* Data Management Setting */}
                <div className={`bg-white rounded-xl border px-[21px] pt-[23px] pb-[32px] border-[#BEBECE]`}>
                    <div className="flex flex-col  justify-between">
                        <div>
                            <div className="flex flex-row justify-between gap-4 flex-1">
                                <div className="flex items-center space-x-5">
                                    <div
                                        className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                            settings.removePreviousData ? 'bg-[#F8D5DB] text-[#FC4C59]' : 'bg-[#ECEBF8] text-mudPurple'
                                        }`}
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                            />
                                        </svg>
                                    </div>
                                    <h4 className="text-[24px] leading-[30px] font-bold text-darkPurple">{t('Data Replacement')}</h4>
                                </div>
                                {/* Switch */}
                                <div
                                    className={`relative inline-flex  h-[30px] w-[55px] items-center rounded-full transition-colors ${
                                        uploadType === 'full_update' ? 'cursor-not-allowed opacity-60' : 'cursor-not-allowed opacity-60'
                                    } bg-[#ECEBF8]`}
                                >
                                    <span
                                        className={`inline-block h-[26px] w-[26px]  transform rounded-full ${
                                            settings.removePreviousData ? 'bg-[#5E639B]' : 'bg-[#BEBECE]'
                                        } transition-transform ${settings.removePreviousData ? 'translate-x-[27px]' : 'translate-x-[2px]'}`}
                                    />
                                </div>
                            </div>
                            <p className="text-[16px] leading-[18px] font-medium text-deepPurple mt-[27px]">
                                {uploadType === 'full_update'
                                    ? t('Clear existing data before importing (required for whole school update)')
                                    : t('Keep existing data and only add new records (required for add-only mode)')}
                            </p>
                        </div>

                        {uploadType === 'full_update' && (
                            <div className="bg-[#FC4C5933] border border-[#FC4C59] rounded-[24px] p-3 flex-1 mt-[27px]">
                                <div className="flex items-center space-x-3">
                                    <div className={`bg-[#FC4C5933] w-[50px] h-[50px] rounded-full flex items-center justify-center`}>
                                        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g clip-path="url(#clip0_205_4169)">
                                                <path
                                                    d="M1.5 3.99959V9.99959M1.5 9.99959H7.5M1.5 9.99959L6.14 5.63959C7.21475 4.5643 8.54437 3.77879 10.0048 3.35636C11.4652 2.93393 13.0089 2.88834 14.4917 3.22385C15.9745 3.55935 17.3482 4.26502 18.4845 5.27501C19.6209 6.28499 20.4828 7.56637 20.99 8.99959M23.5 19.9996V13.9996M23.5 13.9996H17.5M23.5 13.9996L18.86 18.3596C17.7853 19.4349 16.4556 20.2204 14.9952 20.6428C13.5348 21.0652 11.9911 21.1108 10.5083 20.7753C9.02547 20.4398 7.6518 19.7342 6.51547 18.7242C5.37913 17.7142 4.51717 16.4328 4.01 14.9996"
                                                    stroke="#585695"
                                                    stroke-width="2"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_205_4169">
                                                    <rect width="24" height="24" fill="white" transform="translate(0.5)" />
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    </div>
                                    <span className="text-[16px] leading-5 font-bold text-darkPurple">
                                        {t('This will permanently remove users and classes not in your Excel file')}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Password Management Setting */}
                <div className={`bg-white rounded-xl border px-[21px] pt-[23px] pb-[32px] border-[#BEBECE]`}>
                    <div className="flex flex-col  justify-between">
                        <div>
                            <div className="flex flex-row justify-between gap-4 flex-1">
                                <div className="flex items-center space-x-5">
                                    <div
                                        className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                            settings.removePreviousData ? 'bg-[#ECEBF8] text-deepPurple' : 'bg-[#ECEBF8] text-mudPurple'
                                        }`}
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                            />
                                        </svg>
                                    </div>
                                    <h4 className="text-[24px] leading-[30px] font-bold text-darkPurple">{t('Password Reset')}</h4>
                                </div>
                                {/* Switch */}
                                <button
                                    type="button"
                                    onClick={() => handleToggle('promptNewPassword')}
                                    className={`relative inline-flex h-[30px] w-[55px] items-center rounded-full transition-colors bg-[#ECEBF8] `}
                                >
                                    <span
                                        className={`inline-block h-[26px] w-[26px] transform rounded-full bg-white transition-transform ${
                                            settings.promptNewPassword ? '!bg-[#5E639B]' : '!bg-[#BEBECE]'
                                        } ${settings.promptNewPassword ? 'translate-x-[27px]' : 'translate-x-[2px]'}`}
                                    />
                                </button>
                            </div>
                            <p className="text-[16px] leading-[18px] font-medium text-deepPurple mt-[27px]">
                                {t('Force users to change their password on first login for enhanced security')}
                            </p>
                        </div>

                        <div className="bg-[#ECEBF8] border border-[#BEBECE] rounded-[24px] p-3 flex-1 mt-[27px]">
                            <div className="flex items-center space-x-3">
                                <div className={`bg-[#DEDCFB] w-[50px] h-[50px] rounded-full flex items-center justify-center`}>
                                    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <g clip-path="url(#clip0_205_4169)">
                                            <path
                                                d="M1.5 3.99959V9.99959M1.5 9.99959H7.5M1.5 9.99959L6.14 5.63959C7.21475 4.5643 8.54437 3.77879 10.0048 3.35636C11.4652 2.93393 13.0089 2.88834 14.4917 3.22385C15.9745 3.55935 17.3482 4.26502 18.4845 5.27501C19.6209 6.28499 20.4828 7.56637 20.99 8.99959M23.5 19.9996V13.9996M23.5 13.9996H17.5M23.5 13.9996L18.86 18.3596C17.7853 19.4349 16.4556 20.2204 14.9952 20.6428C13.5348 21.0652 11.9911 21.1108 10.5083 20.7753C9.02547 20.4398 7.6518 19.7342 6.51547 18.7242C5.37913 17.7142 4.51717 16.4328 4.01 14.9996"
                                                stroke="#585695"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_205_4169">
                                                <rect width="24" height="24" fill="white" transform="translate(0.5)" />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </div>
                                <span className="text-[16px] leading-5 font-bold text-darkPurple">{t('Recommended for new users and security compliance')}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Excel Password Setting */}
                <div
                    className={`bg-white rounded-xl border px-[21px] pt-[23px] pb-[32px] border-[#BEBECE] lg:col-span-2 ${
                        hasPasswordColumns ? '' : 'opacity-50 cursor-not-allowed'
                    }`}
                >
                    <div className="flex flex-col  justify-between">
                        <div>
                            <div className="flex flex-row justify-between gap-4 flex-1">
                                <div className="flex items-center space-x-5">
                                    <div
                                        className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                            settings.removePreviousData ? 'bg-[#ECEBF8] text-deepPurple' : 'bg-[#ECEBF8] text-mudPurple'
                                        }`}
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                            />
                                        </svg>
                                    </div>
                                    <h4 className="text-[24px] leading-[30px] font-bold text-darkPurple">{t('Use Excel Passwords')}</h4>
                                </div>
                                {/* Switch */}
                                <button
                                    type="button"
                                    disabled={!hasPasswordColumns}
                                    onClick={() => handleToggle('useSheetPassword')}
                                    className={`relative inline-flex h-[30px] w-[55px] items-center rounded-full transition-colors bg-[#ECEBF8] ${
                                        hasPasswordColumns ? '' : 'cursor-not-allowed opacity-50'
                                    } `}
                                >
                                    <span
                                        className={`inline-block h-[26px] w-[26px] transform rounded-full bg-white transition-transform ${
                                            settings.useSheetPassword && hasPasswordColumns ? '!bg-[#5E639B]' : '!bg-[#BEBECE]'
                                        } ${settings.useSheetPassword && hasPasswordColumns ? 'translate-x-[27px]' : 'translate-x-[2px]'}`}
                                    />
                                </button>
                            </div>
                            <p className="text-[16px] leading-[18px] font-medium text-deepPurple mt-[27px]">
                                {t('Use password values from the Excel file if available, otherwise generate secure passwords')}
                            </p>
                        </div>

                        {hasPasswordColumns ? (
                            <div className="bg-[#ECEBF8] border border-[#BEBECE] rounded-[24px] p-3 flex-1 mt-[27px]">
                                <div className="flex items-center space-x-3">
                                    <div className={`bg-[#DEDCFB] w-[50px] h-[50px] rounded-full flex items-center justify-center`}>
                                        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g clip-path="url(#clip0_205_4169)">
                                                <path
                                                    d="M1.5 3.99959V9.99959M1.5 9.99959H7.5M1.5 9.99959L6.14 5.63959C7.21475 4.5643 8.54437 3.77879 10.0048 3.35636C11.4652 2.93393 13.0089 2.88834 14.4917 3.22385C15.9745 3.55935 17.3482 4.26502 18.4845 5.27501C19.6209 6.28499 20.4828 7.56637 20.99 8.99959M23.5 19.9996V13.9996M23.5 13.9996H17.5M23.5 13.9996L18.86 18.3596C17.7853 19.4349 16.4556 20.2204 14.9952 20.6428C13.5348 21.0652 11.9911 21.1108 10.5083 20.7753C9.02547 20.4398 7.6518 19.7342 6.51547 18.7242C5.37913 17.7142 4.51717 16.4328 4.01 14.9996"
                                                    stroke="#585695"
                                                    stroke-width="2"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_205_4169">
                                                    <rect width="24" height="24" fill="white" transform="translate(0.5)" />
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    </div>
                                    <span className="text-[16px] leading-5 font-bold text-darkPurple">{t('Password columns detected')}:</span>
                                    <div className="text-[16px] leading-5 font-bold text-darkPurple">{[studentPasswordColumn, staffPasswordColumn].filter(Boolean).join(', ')}</div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-[#ECEBF8] border border-[#BEBECE] rounded-[24px] p-3 flex-1 mt-[27px]">
                                <div className="flex items-center space-x-3">
                                    <div className={`bg-[#DEDCFB] w-[50px] h-[50px] rounded-full flex items-center justify-center`}>
                                        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g clip-path="url(#clip0_205_4169)">
                                                <path
                                                    d="M1.5 3.99959V9.99959M1.5 9.99959H7.5M1.5 9.99959L6.14 5.63959C7.21475 4.5643 8.54437 3.77879 10.0048 3.35636C11.4652 2.93393 13.0089 2.88834 14.4917 3.22385C15.9745 3.55935 17.3482 4.26502 18.4845 5.27501C19.6209 6.28499 20.4828 7.56637 20.99 8.99959M23.5 19.9996V13.9996M23.5 13.9996H17.5M23.5 13.9996L18.86 18.3596C17.7853 19.4349 16.4556 20.2204 14.9952 20.6428C13.5348 21.0652 11.9911 21.1108 10.5083 20.7753C9.02547 20.4398 7.6518 19.7342 6.51547 18.7242C5.37913 17.7142 4.51717 16.4328 4.01 14.9996"
                                                    stroke="#585695"
                                                    stroke-width="2"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_205_4169">
                                                    <rect width="24" height="24" fill="white" transform="translate(0.5)" />
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    </div>
                                    <span className="text-[16px] leading-5 font-bold text-darkPurple">
                                        {t('No password columns detected in uploaded data. Secure passwords will be auto-generated.')}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Save Passwords to Masterlist Setting*/}

                <div className={`bg-white rounded-xl border px-[21px] pt-[23px] pb-[32px] border-[#BEBECE] lg:col-span-2 mt-6`}>
                    <div className="flex flex-col justify-between">
                        <div>
                            <div className="flex flex-row justify-between gap-4 flex-1">
                                <div className="flex items-center space-x-5">
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center bg-[#ECEBF8] text-deepPurple`}>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                                            />
                                        </svg>
                                    </div>
                                    <h4 className="text-[24px] leading-[30px] font-bold text-darkPurple">{t('Save Passwords to Master list')}</h4>
                                </div>
                                {/* Switch */}
                                <button
                                    type="button"
                                    onClick={() => handleToggle('savePasswordsToMasterList')}
                                    className={`relative inline-flex h-[30px] w-[55px] items-center rounded-full transition-colors bg-[#ECEBF8]`}
                                >
                                    <span
                                        className={`inline-block h-[26px] w-[26px] transform rounded-full bg-white transition-transform ${
                                            settings.savePasswordsToMasterList ? '!bg-[#5E639B]' : '!bg-[#BEBECE]'
                                        } ${settings.savePasswordsToMasterList ? 'translate-x-[27px]' : 'translate-x-[2px]'}`}
                                    />
                                </button>
                            </div>
                            <p className="text-[16px] leading-[18px] font-medium text-deepPurple mt-[27px]">
                                {t('Store passwords from Excel file in a secure master list for administrative purposes')}
                            </p>
                        </div>

                        <div className="bg-[#ECEBF8] border border-[#BEBECE] rounded-[24px] p-3 flex-1 mt-[27px]">
                            <div className="flex items-center space-x-3">
                                <div className={`bg-[#DEDCFB] w-[50px] h-[50px] rounded-full flex items-center justify-center`}>
                                    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M12.5 15l2 2 4-4m1-9H6.5a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9.5 5a2 2 0 002 2h2a2 2 0 002-2M9.5 5a2 2 0 012-2h2a2 2 0 012 2"
                                            stroke="#585695"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            fill="none"
                                        />
                                    </svg>
                                </div>
                                <span className="text-[16px] leading-5 font-bold text-darkPurple">
                                    {t('Passwords will be saved securely and can be accessed by administrators when needed')}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Data Requirements */}
            {/* <div className="bg-[#F8F8FB] rounded-[16px] py-9 px-[33px]  mt-[56px]">
                <div className="flex items-start space-x-4">
                    <div className="flex-1">
                        <div className="flex flex-row items-center gap-4">
                            <div className="w-14 h-14 rounded-full flex items-center justify-center border border-darkPurple ">
                                <svg className="w-6 h-6 text-darkPurple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                    />
                                </svg>
                            </div>
                            <h4 className="text-[28px] font-bold  text-darkPurple tracking-tight ">{t('Excel File Requirements')}</h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-[42px] ml-[73px]">
                            <div className="space-y-[21px]">
                                <p className="font-bold text-[24px] leading-5 text-darkPurple">{t('Student Data')}</p>

                                <ul className="text-[16px] leading-[18px] font-medium text-deepPurple space-y-[18px] ">
                                    {[
                                        t('Student ID (required)'),
                                        t('Full Name (required)'),
                                        t('Class Assignment (required)'),
                                        t('Email (required for secondary)'),
                                        t('Gender (optional)'),
                                        t('Password (optional)'),
                                    ].map((req, index) => (
                                        <li key={index}>• {req}</li>
                                    ))}
                                </ul>
                            </div>
                            <div className="space-y-[21px]">
                                <p className="font-bold text-[24px] leading-5 text-darkPurple">{t('Staff Data')}</p>

                                <ul className="text-[16px] leading-[18px] font-medium text-deepPurple space-y-[18px] ">
                                    {[
                                        t('Staff ID (required)'),
                                        t('Full Name (required)'),
                                        t('Email (required)'),
                                        t('Authority/Role (required)'),
                                        t('Department (optional)'),
                                        t('Password (optional)'),
                                    ].map((req, index) => (
                                        <li key={index}>• {req}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div className="bg-[#ECEBF8] border border-[#BEBECE] rounded-[24px] p-3 flex-1 mt-[36px] ml-[73px]">
                            <div className="flex items-center space-x-3">
                                <div className={`bg-[#DEDCFB] w-[50px] h-[50px] rounded-full flex items-center justify-center`}>
                                    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <g clip-path="url(#clip0_205_4169)">
                                            <path
                                                d="M1.5 3.99959V9.99959M1.5 9.99959H7.5M1.5 9.99959L6.14 5.63959C7.21475 4.5643 8.54437 3.77879 10.0048 3.35636C11.4652 2.93393 13.0089 2.88834 14.4917 3.22385C15.9745 3.55935 17.3482 4.26502 18.4845 5.27501C19.6209 6.28499 20.4828 7.56637 20.99 8.99959M23.5 19.9996V13.9996M23.5 13.9996H17.5M23.5 13.9996L18.86 18.3596C17.7853 19.4349 16.4556 20.2204 14.9952 20.6428C13.5348 21.0652 11.9911 21.1108 10.5083 20.7753C9.02547 20.4398 7.6518 19.7342 6.51547 18.7242C5.37913 17.7142 4.51717 16.4328 4.01 14.9996"
                                                stroke="#585695"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_205_4169">
                                                <rect width="24" height="24" fill="white" transform="translate(0.5)" />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </div>
                                <div className="space-y-3">
                                    <p className="text-[16px] leading-5 font-bold text-darkPurple">
                                        {uploadType === 'full_update' ? t('Full Update Requirements') : t('Add-Only Requirements')}
                                    </p>
                                    <p className="text-[16px] leading-[18px] font-medium text-deepPurple">
                                        {uploadType === 'full_update'
                                            ? t('Your Excel file should contain ALL current students and staff. Missing users will be disabled.')
                                            : t('Your Excel file should only contain NEW students and staff to be added to the system.')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div> */}
        </div>
    )
}

export default SettingsTab
